import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, LogOut } from 'lucide-react';
import { useFamilyMode } from '../../context/FamilyModeContext';
import ParentalPINModal from './ParentalPINModal';
import AlternativeAuthModal from './AlternativeAuthModal';

export default function FamilyModeExitModal() {
  const { t } = useTranslation();
  const { 
    exitModalOpen, 
    closeExitModal, 
    disableDirectly,
    pinModalOpen,
    pinModalMode,
    closePINModal,
    openPINModal,
    hasPINSetup,
    requirePIN
  } = useFamilyMode();

  const [showAlternativeMethods, setShowAlternativeMethods] = useState(false);

  const handleDirectExit = () => {
    // Salir sin PIN (cuando no está configurado o requirePIN = false)
    disableDirectly();
    closeExitModal();
  };

  const handlePINValidated = () => {
    // Al validar PIN correctamente, salir directamente
    closePINModal();
    disableDirectly();
    closeExitModal();
  };

  // Si tiene PIN configurado, SIEMPRE pedirlo (sin importar requirePIN)
  const showPINDirectly = hasPINSetup && !showAlternativeMethods;
  useEffect(() => {
    if (!exitModalOpen) return;
    if (!showPINDirectly) return;
    if (pinModalOpen) return;
    openPINModal('verify', handlePINValidated);
  }, [exitModalOpen, showPINDirectly, pinModalOpen]);

  if (!exitModalOpen) return null;

  return (
    <>
      {/* Modal PIN directo (cuando está activado) */}
      {showPINDirectly && (
        <ParentalPINModal
          isOpen={exitModalOpen}
          onClose={() => {
            closeExitModal();
            setShowAlternativeMethods(false);
          }}
          onSuccess={handlePINValidated}
          title="🔒 PIN Parental"
          description="Ingresa el PIN para salir del Modo Familiar"
          mode="verify"
        >
          {/* Botón de métodos alternativos dentro del modal PIN */}
          <button
            onClick={() => setShowAlternativeMethods(true)}
            className="w-full mt-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors underline"
          >
            Probar otros métodos
          </button>
        </ParentalPINModal>
      )}

      {/* Modal de confirmación simple (cuando NO tiene PIN configurado) */}
      {!hasPINSetup && !showAlternativeMethods && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-black/50" onClick={closeExitModal}>
          <div
            className="w-full max-w-md rounded-[24px] shadow-sm max-h-[80vh] overflow-hidden flex flex-col"
            style={{ backgroundColor: '#fff5eb' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between p-5 rounded-t-[24px]"
              style={{ backgroundColor: '#055590' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <LogOut className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-black text-white">
                  {t('common.exit', { defaultValue: 'Salir' })}
                </h2>
              </div>
              <button
                onClick={closeExitModal}
                className="w-8 h-8 rounded-2xl hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                {t('familyMode.exitDescription', { defaultValue: 'Autentícate con tu cuenta para salir del Modo Grupo' })}
              </p>

              <button
                onClick={() => setShowAlternativeMethods(true)}
                className="w-full p-4 rounded-2xl font-black shadow-sm transition-all flex items-center justify-center gap-3"
                style={{ backgroundColor: '#ef6e6b', color: '#ffffff' }}
              >
                <LogOut className="w-5 h-5" />
                {t('familyMode.authenticate', { defaultValue: 'Autenticarme' })}
              </button>

              <button
                onClick={() => {
                  closeExitModal();
                  setShowAlternativeMethods(false);
                }}
                className="w-full p-4 rounded-2xl font-black shadow-sm transition-all flex items-center justify-center gap-3"
                style={{ backgroundColor: '#ffffff', opacity: 0.8 }}
              >
                {t('common.cancel', { defaultValue: 'Cancelar' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de autenticación alternativa (Email/Google) */}
      <AlternativeAuthModal
        isOpen={showAlternativeMethods}
        onClose={() => setShowAlternativeMethods(false)}
        onSuccess={() => {
          setShowAlternativeMethods(false);
          handleDirectExit();
        }}
      />
    </>
  );
}
